/**
 * The chart toolbar's drawing-tools dropdown.
 *
 * Mirrors the floating `DrawingBar` on the chart's left edge, laid out for a
 * dropdown instead of a 36px rail: one row per CATEGORY (the groups from
 * `OVERLAY_GROUPS`, then the bar's mode controls), each opening its own tools
 * in a flyout to the side on hover. Listing every tool flat instead would run
 * to fifty-odd rows, which is longer than most charts are tall.
 *
 * Both surfaces read `OVERLAY_GROUPS`, `ToolIcons`, `TOOL_HOTKEYS`, the shared
 * icon set in `drawingBarChrome` and the same `Flyout`, so neither can drift
 * from the other.
 *
 * By default it renders the Toolbar's portaled `Popover` with NO wrapping
 * element around it: that popover measures its own parent to place itself, so
 * anything between it and the trigger button would anchor the menu to the
 * wrong box. Hosts that bring their own popover pass `inline` instead - the
 * flyouts are fixed-positioned and offset-corrected, so they escape that
 * popover's bounds rather than being clipped by it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { OVERLAY_GROUPS } from '../utils/overlays'
import { ToolIcons } from './toolIcons'
import { TOOL_HOTKEYS } from './toolHotkeys'
import { Popover } from './toolbarPopover'
import { Flyout, FlyoutItem } from './toolFlyout'
import { ControlIcons, GroupIcons, HIDE_MODE_LABELS, type HideMode } from './drawingBarChrome'

/** Hover intent, matching the DrawingBar's group buttons. */
const HOVER_DELAY_MS = 160

export interface DrawingToolsMenuProps {
  onSelectTool: (overlayName: string) => void
  /** Overlay name of the armed tool, or null. Drives the single highlight. */
  activeTool?: string | null
  onSnapToggle?: () => void
  snapActive?: boolean
  onLockAll?: () => void
  lockActive?: boolean
  onTrashAll?: () => void
  onSetHidden?: (mode: HideMode, hidden: boolean) => void
  hiddenState?: { drawings: boolean; indicators: boolean }
  /** Called after any selection, so the toolbar can close the dropdown. */
  onClose?: () => void
  /**
   * Render the rows ALONE, with no popover around them, for a host that
   * already supplies its own (the workspace panel header puts this inside a
   * Radix popover). Same opt-out `IndicatorPicker` offers, for the same reason.
   */
  inline?: boolean
  /**
   * Favorited overlay names. With `onToggleFavorite`, every tool row grows a
   * star (filled when favorited) that toggles membership WITHOUT arming the
   * tool or closing the menu. The floating DrawingBar shows these as its
   * flat shortlist.
   */
  favorites?: string[]
  onToggleFavorite?: (overlayName: string) => void
}

/**
 * The handler bundle a chart view publishes for its drawing surfaces: what
 * the floating DrawingBar and the toolbar / header DrawingToolsMenu share.
 */
export type DrawingToolsBundle = Omit<DrawingToolsMenuProps, 'onClose' | 'inline'>

export function DrawingToolsMenu({
  onSelectTool,
  activeTool,
  onSnapToggle,
  snapActive,
  onLockAll,
  lockActive,
  onTrashAll,
  onSetHidden,
  hiddenState,
  onClose,
  inline,
  favorites,
  onToggleFavorite,
}: DrawingToolsMenuProps) {
  // One open submenu at a time, keyed by row. Same single-slot model the bar
  // uses, so moving between categories swaps the flyout rather than stacking.
  const [openRow, setOpenRow] = useState<string | null>(null)

  // Only the category owning the armed tool lights up - the bar's rule.
  const activeGroupLabel = activeTool
    ? (OVERLAY_GROUPS.find((g) => g.tools.some((t) => t.name === activeTool))?.label ?? null)
    : null

  // What "hidden" means for a mode - mirrors DrawingBar's own reading of the
  // same state, so the eye rows here and there agree on open vs closed.
  const isModeHidden = (mode: HideMode): boolean => {
    if (!hiddenState) return false
    if (mode === 'drawings') return hiddenState.drawings
    if (mode === 'indicators') return hiddenState.indicators
    return hiddenState.drawings && hiddenState.indicators
  }
  const anyHidden = isModeHidden('drawings') || isModeHidden('indicators')

  const hasControls = !!(onSnapToggle || onLockAll || onSetHidden || onTrashAll)

  const body = (
    <div
      data-testid="drawing-tools-menu"
      className={
        inline ? 'max-h-[70vh] overflow-y-auto' : 'max-h-[70vh] w-[140px] overflow-y-auto p-1'
      }
    >
      {OVERLAY_GROUPS.map((group) => (
        <SubmenuRow
          key={group.label}
          icon={GroupIcons[group.label]}
          label={group.label}
          active={activeGroupLabel === group.label}
          isOpen={openRow === group.label}
          onOpen={() => setOpenRow(group.label)}
          onClose={() => setOpenRow((r) => (r === group.label ? null : r))}
        >
          {group.tools.map((tool) => (
            <FlyoutItem
              key={tool.name}
              icon={ToolIcons[tool.name]}
              active={activeTool === tool.name}
              hotkey={TOOL_HOTKEYS[tool.name]}
              onClick={() => {
                onSelectTool(tool.name)
                onClose?.()
              }}
              trailing={
                onToggleFavorite ? (
                  <FavoriteStar
                    label={tool.label}
                    favorited={!!favorites?.includes(tool.name)}
                    onToggle={() => onToggleFavorite(tool.name)}
                  />
                ) : undefined
              }
            >
              {tool.label}
            </FlyoutItem>
          ))}
        </SubmenuRow>
      ))}

      {hasControls && (
        <>
          <div className="my-1 h-px bg-border-strong" />

          {onSnapToggle && (
            <PlainRow
              icon={snapActive ? ControlIcons.magnetOn : ControlIcons.magnetOff}
              active={snapActive}
              onClick={() => {
                onSnapToggle()
                onClose?.()
              }}
            >
              {snapActive ? 'Magnet on' : 'Magnet'}
            </PlainRow>
          )}

          {onLockAll && (
            <PlainRow
              icon={lockActive ? ControlIcons.lock : ControlIcons.unlock}
              active={lockActive}
              onClick={() => {
                onLockAll()
                onClose?.()
              }}
            >
              {lockActive ? 'Unlock All' : 'Lock All'}
            </PlainRow>
          )}

          {/* Hide is a submenu too: three targets (drawings / indicators /
              both), which is one more than a single row can say. */}
          {onSetHidden && (
            <SubmenuRow
              icon={anyHidden ? ControlIcons.invisible : ControlIcons.visible}
              label="Hide"
              active={anyHidden}
              isOpen={openRow === 'hide'}
              onOpen={() => setOpenRow('hide')}
              onClose={() => setOpenRow((r) => (r === 'hide' ? null : r))}
            >
              {(['drawings', 'indicators', 'both'] as HideMode[]).map((mode) => {
                const hidden = isModeHidden(mode)
                return (
                  <FlyoutItem
                    key={mode}
                    icon={hidden ? ControlIcons.invisible : ControlIcons.visible}
                    active={hidden}
                    onClick={() => {
                      onSetHidden(mode, !hidden)
                      onClose?.()
                    }}
                  >
                    {hidden ? 'Show ' : 'Hide '}
                    {HIDE_MODE_LABELS[mode]}
                  </FlyoutItem>
                )
              })}
            </SubmenuRow>
          )}

          {onTrashAll && (
            <PlainRow
              icon={ControlIcons.remove}
              onClick={() => {
                onTrashAll()
                onClose?.()
              }}
            >
              Remove All
            </PlainRow>
          )}
        </>
      )}
    </div>
  )

  if (inline) return body
  return (
    <Popover anchor="bottom" align="left">
      {body}
    </Popover>
  )
}

/**
 * The favorites star on a tool row. Visible on row hover and whenever
 * favorited, so the catalog reads clean but the shortlist is always legible.
 */
function FavoriteStar({
  label,
  favorited,
  onToggle,
}: {
  label: string
  favorited: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      aria-label={`${favorited ? 'Unfavorite' : 'Favorite'} ${label}`}
      title={favorited ? 'Remove from the drawing rail' : 'Pin to the drawing rail'}
      data-favorited={favorited ? 'true' : 'false'}
      onClick={(e) => {
        e.stopPropagation()
        onToggle()
      }}
      className={
        'flex h-5 w-5 items-center justify-center rounded-sm chrome-press hover:bg-accent ' +
        (favorited
          ? 'text-primary'
          : 'text-[hsl(var(--chart-text)/0.5)] opacity-0 group-hover/flyout-item:opacity-100 hover:text-[hsl(var(--chart-text)/0.9)]')
      }
    >
      <svg
        width={13}
        height={13}
        viewBox="0 0 24 24"
        fill={favorited ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z" />
      </svg>
    </button>
  )
}

const ROW_CLASS =
  'flex w-full items-center gap-2 whitespace-nowrap rounded-sm px-2 py-1 text-left text-xs chrome-press '

/** Shared row tone. Highlight matches DrawingBar's `bg-accent text-primary`. */
function rowTone(active?: boolean, isOpen?: boolean) {
  if (isOpen || active) return 'bg-accent text-primary'
  return 'text-[hsl(var(--chart-text)/0.72)] chrome-press hover:bg-accent hover:text-[hsl(var(--chart-text)/0.9)]'
}

/**
 * A row that opens a flyout of its own on hover, like a shadcn submenu.
 *
 * The flyout is a DESCENDANT of this row's wrapper, so moving the pointer from
 * the row onto the flyout keeps the wrapper "entered" and only leaving both
 * schedules the (cancellable) close. That is what makes the diagonal trip to a
 * submenu survivable.
 */
function SubmenuRow({
  icon,
  label,
  active,
  isOpen,
  onOpen,
  onClose,
  children,
}: {
  icon?: React.ReactNode
  label: string
  active?: boolean
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  children: React.ReactNode
}) {
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current)
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])
  useEffect(() => clearTimers, [clearTimers])

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    openTimer.current = setTimeout(onOpen, HOVER_DELAY_MS)
  }
  const handleLeave = () => {
    if (openTimer.current) clearTimeout(openTimer.current)
    closeTimer.current = setTimeout(onClose, HOVER_DELAY_MS)
  }

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={label}
        // Click opens it too, so the menu works without hover intent
        // (touch, and anyone driving it deliberately rather than by sweep).
        onClick={() => (isOpen ? onClose() : onOpen())}
        className={ROW_CLASS + rowTone(active, isOpen)}
      >
        <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
          {icon}
        </span>
        <span className="grow">{label}</span>
        <SubmenuCaret />
      </button>
      {isOpen && <Flyout>{children}</Flyout>}
    </div>
  )
}

/** Right-pointing chevron marking a row that opens a submenu. */
function SubmenuCaret() {
  return (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0 opacity-70"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

/** A row that just acts, with no submenu. */
function PlainRow({
  children,
  icon,
  active,
  onClick,
}: {
  children: React.ReactNode
  icon?: React.ReactNode
  active?: boolean
  onClick?: () => void
}) {
  return (
    <button type="button" onClick={onClick} className={ROW_CLASS + rowTone(active)}>
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
        {icon}
      </span>
      <span className="grow">{children}</span>
    </button>
  )
}
